import type { IntlShape } from "react-intl";
import type { IngestedFile } from "@/lib/file-ingest";

// The form state of an image or attachment list in Share (a post's images and attachments, a
// listing's images): fresh uploads and files that already live on the entity, side by side in one
// list. Shared by ShareCreatePage and the listing form, which both render it through FileDrop.

export interface UploadedFile {
  document?: import("@/lib/document-resource").DocumentResource;
  name: string;
  mimeType: string;
  content: string;
  preview: string;
  existingId?: string;
  /** Where an existing file lives on the item today (mainImage vs. gallery). */
  existingRole?: "main" | "gallery";
  /** Selected as the item's mainImage (Vorschaubild). Exactly one per image list. */
  isMain?: boolean;
}

/** A stored image as genesis returns it: `File { id mimeType content }`. */
export interface StoredImage {
  id: string;
  mimeType?: string | null;
  content?: string | null;
}

/** Exactly one image carries isMain; defaults to the first one. */
export function normalizeMain(list: UploadedFile[]): UploadedFile[] {
  if (list.length === 0) return list;
  const mains = list.filter((f) => f.isMain).length;
  if (mains === 1) return list;
  if (mains === 0) return list.map((f, i) => (i === 0 ? { ...f, isMain: true } : f));
  let seen = false;
  return list.map((f) => {
    if (!f.isMain) return f;
    if (seen) return { ...f, isMain: false };
    seen = true;
    return f;
  });
}

export function toUploadedFile(f: IngestedFile): UploadedFile {
  return {
    name: f.name,
    mimeType: f.mimeType,
    content: f.content,
    preview: `data:${f.mimeType};base64,${f.content}`,
  };
}

/**
 * The image list an editor starts from: the stored mainImage first, then the further images in their
 * order. Pure, so a form can seed useState from it instead of a prefill effect (#78). A further image
 * whose bytes did not come back still gets its tile (with the file glyph): leaving it out would read
 * as removed and delete it on the next save.
 */
export function storedImages(
  item: { mainImage?: StoredImage | null; images?: readonly StoredImage[] | null } | null,
  intl: IntlShape,
): UploadedFile[] {
  if (!item) return [];
  const galleryImages: UploadedFile[] = (item.images ?? []).map((f, i) => ({
    name: intl.formatMessage(
      { id: "share.create.existingImage", defaultMessage: "Image {n}" },
      { n: i + 1 },
    ),
    mimeType: f.mimeType ?? "application/octet-stream",
    content: f.content ?? "",
    preview: f.content ? `data:${f.mimeType};base64,${f.content}` : "",
    existingId: f.id,
    existingRole: "gallery" as const,
  }));
  const mainImage: UploadedFile[] = item.mainImage?.content
    ? [
        {
          name: intl.formatMessage({
            id: "share.create.mainImageBadge",
            defaultMessage: "Preview image",
          }),
          mimeType: item.mainImage.mimeType ?? "application/octet-stream",
          content: item.mainImage.content,
          preview: `data:${item.mainImage.mimeType};base64,${item.mainImage.content}`,
          existingId: item.mainImage.id,
          existingRole: "main" as const,
          isMain: true,
        },
      ]
    : [];
  return normalizeMain([...mainImage, ...galleryImages]);
}
